// Grupo de un paquete dentro del ledger.
//
// El patrón visual viene de PackageBlock.jsx de Recepción Digital — cabecera
// con el nombre editable inline, subtotal a la derecha, encabezado de columnas
// una sola vez, filas de artículos y un "añadir" al pie del grupo. Se construyó
// de cero acá para no acoplar Desarrollos a Comando Ops: allá el paquete lleva
// destinatario + unique_package_id validado, chip de impuesto y menús de mover
// y combinar; acá el paquete es solo un nombre y un orden.
//
// Sin borrar paquete y sin reordenar: sort_order se fija al crear. Post-expo.
//
// Render-props opcionales (aditivos, default null — sin ellos el HTML es
// idéntico byte a byte al previo):
//   · renderPaqueteExtras(paquete): controles extra bajo la cabecera del
//     paquete (la Encomienda de viajero inyecta cliente / bulto / tipo_ingreso).
//   · renderItemExtras(item, paquete): tira bajo cada fila de artículo (el
//     viajero inyecta el estado Nuevo/Usado y los chips de procedencia).
//   · encabezadoFijo (default false): el encabezado de columnas queda sticky
//     arriba mientras se recorre el paquete (fondo opaco, sobre las filas).

import { useEffect, useRef } from 'react';
import { Plus } from 'lucide-react';
import AnexoItemRow from './AnexoItemRow';
import { ANEXO_ITEM_GRID } from './grid';

const PackageGroup = ({
  paquete,
  items,
  autoFocusNombre,
  ultimoAgregadoId,
  onChangeNombre,
  onChangeItem,
  onQuitarItem,
  onAgregarItem,
  renderPaqueteExtras = null,
  renderItemExtras = null,
  encabezadoFijo = false,
}) => {
  const nombreRef = useRef(null);

  useEffect(() => {
    if (autoFocusNombre) nombreRef.current?.focus();
  }, [autoFocusNombre]);

  const subtotal = items.reduce(
    (s, it) => s + (Number(it.cantidad) || 0) * (Number(it.fob_unitario_usd) || 0),
    0
  );

  return (
    <section
      className="mb-3 rounded-lg border border-slate-200"
      aria-label={`Paquete ${paquete.nombre || 'sin nombre'}`}
    >
      {/* Cabecera: nombre editable + subtotal */}
      <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
        <label className="flex flex-1 items-center gap-1.5 text-[11px] text-slate-500">
          Paquete
          <input
            ref={nombreRef}
            type="text"
            value={paquete.nombre || ''}
            onChange={(e) => onChangeNombre(paquete.id, e.target.value)}
            placeholder="Nombre del destinatario"
            aria-label="Nombre del paquete"
            className="min-w-0 flex-1 rounded-md border border-slate-200 bg-[#f8fafc] px-2 py-1.5 text-xs font-medium text-slate-900 outline-none transition-colors focus:border-[var(--brand-teal)]"
          />
        </label>
        <span className="shrink-0 font-mono text-xs font-semibold text-slate-900">
          Subtotal: {subtotal.toFixed(2)}
        </span>
      </div>
      {renderPaqueteExtras ? renderPaqueteExtras(paquete) : null}

      {/* Encabezado de columnas — mismo grid que la fila */}
      <div
        className={`grid ${ANEXO_ITEM_GRID} gap-2 px-1.5 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-normal text-slate-400${
          encabezadoFijo ? ' sticky top-0 z-10 bg-white' : ''
        }`}
        aria-hidden="true"
      >
        <span className="overflow-hidden text-ellipsis whitespace-nowrap">Descripción</span>
        <span className="text-right">Cant.</span>
        <span>Unidad</span>
        <span className="text-right">FOB unit.</span>
        <span className="text-right">FOB total</span>
        <span />
      </div>

      <div className="px-1.5">
        {items.length === 0 ? (
          <p className="px-2 py-4 text-center text-xs text-slate-400">
            Este paquete todavía no tiene artículos.
          </p>
        ) : (
          items.map((it) =>
            renderItemExtras ? (
              <div key={it.id}>
                <AnexoItemRow
                  item={it}
                  autoFocus={it.id === ultimoAgregadoId}
                  onChange={(cambios) => onChangeItem(it.id, cambios)}
                  onQuitar={() => onQuitarItem(it.id)}
                  onEnterNewRow={() => onAgregarItem(paquete.id)}
                />
                {renderItemExtras(it, paquete)}
              </div>
            ) : (
              <AnexoItemRow
                key={it.id}
                item={it}
                autoFocus={it.id === ultimoAgregadoId}
                onChange={(cambios) => onChangeItem(it.id, cambios)}
                onQuitar={() => onQuitarItem(it.id)}
                onEnterNewRow={() => onAgregarItem(paquete.id)}
              />
            )
          )
        )}
      </div>

      <div className="border-t border-slate-200 px-3 py-1.5">
        <button
          type="button"
          onClick={() => onAgregarItem(paquete.id)}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md px-2 text-xs font-medium text-slate-500 hover:bg-slate-50"
        >
          <Plus size={14} aria-hidden="true" /> Añadir ítem
        </button>
      </div>
    </section>
  );
};

export default PackageGroup;
